import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { appLogger, ErrorCategory } from './errorLogger';

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipErrorLogging?: boolean;
}

// Attach to a specific axios instance. The app's `api` instance (utils/api.ts)
// and the default axios object are separate instances — interceptors do NOT
// propagate, so both must be wired explicitly or most API errors never reach
// the logger.
export const setupAxiosErrorInterceptor = (instance: AxiosInstance = axios): (() => void) => {
  const interceptorId = instance.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
      const config = error.config as ExtendedAxiosRequestConfig;
      const response = error.response;
      const message = error.message;
      const statusCode = response?.status;

      // CRITICAL: Don't log errors from the error logger itself (prevents infinite loop)
      if (config?.url?.includes('/error-logs')) {
        return Promise.reject(error);
      }

      // Skip logging for expected 400-level errors
      const skip400Errors = config?.skipErrorLogging || false;
      if (skip400Errors && statusCode && statusCode >= 400 && statusCode < 500) {
        return Promise.reject(error);
      }

      // Determine category
      let category;
      if (statusCode && statusCode >= 500) {
        category = ErrorCategory.SERVER;
      } else if (statusCode === 401 || statusCode === 403) {
        category = ErrorCategory.AUTH;
      } else if (!navigator.onLine) {
        category = ErrorCategory.NETWORK;
      } else {
        category = ErrorCategory.CLIENT;
      }

      // Do NOT forward response.data or request bodies — they can carry
      // clinical note text, credentials and PII. The server-side error log
      // already captures the sanitised request payload; the client sends
      // classification + correlation context only.
      appLogger.error(message || 'API Request Failed', {
        metadata: {
          url: config?.url,
          method: config?.method,
          status: statusCode,
          statusText: response?.statusText
        },
        source: 'axios-interceptor',
        statusCode,
        category,
        path: config?.url,
        method: config?.method?.toUpperCase(),
        // Server echoes the request-scoped correlation ID on every response
        // (X-Request-ID is in CORS exposedHeaders) — join client and server
        // records on it.
        requestId: response?.headers?.['x-request-id'] as string | undefined
      });

      return Promise.reject(error);
    }
  );

  return () => instance.interceptors.response.eject(interceptorId);
};

export default setupAxiosErrorInterceptor;
